import { useState } from "react"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { errorToast, toast } from "@/v2-ui/toast"
import Button, {
  INPUT_ALIGNED_BUTTON_CLASSES,
} from "@/v2-backoffice-client/components/button"
import BasemapThumbnail from "@/v2-backoffice-client/basemap/BasemapThumbnail"
import { useBasemapUpdateMutation } from "@/v2-backoffice-client/basemap/update/basemap.update.mutation"
import type { BasemapFragment } from "@/v2-backoffice-client/basemap/__types__/basemap.fragment"

export type BasemapViewDetailsExtrasProps = {
  basemap: BasemapFragment
}

/**
 * Chooser presentation: the tile the console shows and the position it
 * shows it in.
 *
 * The thumbnail is read-only here on purpose — the runtime column is
 * re-derived from Configuration.Properties.thumbnail on every save, so an
 * edit in this form would be overwritten by the next one. The Configuration
 * tab is where it changes.
 */
function BasemapViewDetailsExtras(props: BasemapViewDetailsExtrasProps) {
  const { basemap } = props

  const [priority, setPriority] = useState(String(basemap.Priority))

  const [updateBasemap, updateState] = useBasemapUpdateMutation()
  errorToast(updateState.error, { title: "Failed to update the position" })

  const parsedPriority = Number(priority)
  const isValid = priority.trim() !== "" && Number.isInteger(parsedPriority)
  const isDirty = isValid && parsedPriority !== basemap.Priority

  async function onSave() {
    const result = await updateBasemap({
      variables: {
        input: {
          FeatureCode: basemap.FeatureCode,
          Priority: parsedPriority,
        },
      },
    })
    if (result.data) toast({ title: "Position saved." })
  }

  return (
    <section className="flex flex-wrap items-start gap-x-10 gap-y-4">
      <div className="flex flex-col gap-1.5">
        <Label>Chooser tile</Label>
        <div className="flex items-center gap-3">
          <BasemapThumbnail
            src={basemap.Thumbnail}
            name={basemap.Name}
            className="h-16 w-24"
          />
          <p className="max-w-56 text-xs text-muted-foreground">
            Taken from <code className="font-mono">Properties.thumbnail</code>{" "}
            in the Configuration tab.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="basemap-details-priority">Position</Label>
        <div className="flex items-center gap-2">
          <Input
            id="basemap-details-priority"
            className="w-24"
            type="number"
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            className={INPUT_ALIGNED_BUTTON_CLASSES}
            isLoading={updateState.loading}
            disabled={!isDirty}
            data-feature-code="UI_BackofficeBasemaps_Details_SavePriority"
            onClick={() => void onSave()}
          >
            Save
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Low numbers come first in the console&apos;s basemap chooser.
        </p>
      </div>
    </section>
  )
}

export default BasemapViewDetailsExtras
